
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Link } from 'react-router-dom';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });
    // In production, you could log this to an error reporting service
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                <CardTitle>Something went wrong</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                We encountered an unexpected error. Please try again or contact support if the issue persists.
              </p>
              {this.state.error && (
                <div className="bg-muted p-3 rounded-md overflow-auto max-h-40 text-xs mb-3">
                  <p className="font-medium">Error details:</p>
                  <p className="text-red-500">{this.state.error.message}</p>
                  {this.state.error.stack && process.env.NODE_ENV !== 'production' && (
                    <pre className="text-xs overflow-auto mt-2 text-muted-foreground">
                      {this.state.error.stack.split('\n').slice(0, 3).join('\n')}
                    </pre>
                  )}
                </div>
              )}
              {this.state.errorInfo && process.env.NODE_ENV !== 'production' && (
                <div className="bg-muted p-3 rounded-md overflow-auto max-h-40 text-xs">
                  <p className="font-medium">Component stack:</p>
                  <pre className="text-xs overflow-auto mt-1 text-muted-foreground">
                    {this.state.errorInfo.componentStack.split('\n').slice(0, 5).join('\n')}
                  </pre>
                </div>
              )}
            </CardContent>
            <CardFooter className="flex flex-col gap-2">
              <Button onClick={this.handleRetry} className="w-full">
                <RefreshCw className="w-4 h-4 mr-2" /> Try Again
              </Button>
              <div className="flex w-full gap-2">
                <Button onClick={this.handleReload} variant="outline" className="flex-1">
                  Reload Page
                </Button>
                <Button variant="outline" className="flex-1" asChild>
                  <Link to="/">
                    <Home className="w-4 h-4 mr-2" /> Home
                  </Link>
                </Button>
              </div>
            </CardFooter>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
